<script setup>
import {reactive, onMounted, computed} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import JsonCSV from 'vue-json-csv';
import {ToastUtil} from "@/Utils/ToastUtil";

const data = reactive({
  search: '',
  headers: [
    {
      title: 'Client',
      align: 'start',
      sortable: true,
      key: 'client',
    },
    ...
        [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December"
        ].map(month => ({title: month, key: month.toLowerCase(), sortable: true})),
    {
      title: 'total',
      sortable: true,
      key: 'total',
    },
  ],
  items: [],
  dataLoading: false,
  loading: false,
  years: [],
  selectedYear: new Date().getFullYear(),
  currentYear: new Date().getFullYear(),
});

const csvData = computed(()=>{
  return data.person.map((item) => ({
    ...item,
    name: item.name,
    date: item.date,
    createdby: item.createdby,
    comment: item.comment
  }))
})

const csvExport = (arrData) => {
  // CSV File
  let csvContent = 'data:text/csv;charset=utf-8,'
  csvContent += [
    Object.keys(arrData[0]).join(';'),
    ...arrData.map((item) => Object.values(item).join(';')),
  ]
      .join('\n')
      .replace(/(^\[)|(\]$)/gm, '')

  const data = encodeURI(csvContent)
  const link = document.createElement('a')
  link.setAttribute('href', data)
  link.setAttribute('download', 'export.csv')
  link.click()
}

const formatCurrency = (num) => {
  let currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD'})
  return currencyFormatter.format(num)
}

const sumField = (key) => {
  if( ! Array.isArray(data.items) ) {
    return 0
  }
    return data.items.reduce((a, b) => a + (b[key] || 0), 0)
}

const listClientRevenue = () => {
  data.dataLoading = true
  ApiWrapper.post('report-client-revenue','list-client-revenue', {year: data.selectedYear})
      .then((response) => {
        if(response.data){
          data.items = response.data;
        }
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(() =>{
        data.dataLoading = false
      })
}

onMounted(()=>{
  data.years = [0,1,2,3,4,5,6,7,8,9].map(interval => (data.currentYear-interval)) // allow selecting the last 10 years
  listClientRevenue()
})

</script>

<template>
  <v-container fluid>
    <v-card class="elevation-2" height="auto" width="auto">
      <v-toolbar flat color="white">
        <v-toolbar-title>Client Revenue (Monthly)</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        >
        </v-progress-linear>
        <v-divider class="mx-4" inset vertical></v-divider>
        <JsonCSV
            :data="data.items"
            name="Client Revenue Report.csv"
            :labels="data.labels"
        >
          <v-icon> mdi-file-download </v-icon>
        </JsonCSV>
        <v-spacer></v-spacer>


      </v-toolbar>
      <v-row class="mx-4">
        <v-col
            class="d-flex justify-end"
            cols="3"
            sm="3"
        >
          <v-select
              :items="data.years"
              v-model="data.selectedYear"
              label="Select Year"
              @update:modelValue="data.selectedYear=$event;listClientRevenue();"
          ></v-select>
        </v-col>
        <v-col
            class="d-flex justify-end"
            cols="3"
            sm="3"
        >
          <v-text-field
              v-model="data.search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
          ></v-text-field>
        </v-col>
      </v-row>
      <v-data-table
          :headers="data.headers"
          :items="data.items"
          :search="data.search"
          class="elevation-1"
          :loading="data.dataLoading"
          loading-text="Loading... Please wait"
      >
        <template v-slot:[`body.prepend`]="{}">
          <tr>
            <th>Totals</th>
            <th>{{ formatCurrency(sumField('january'))     }}</th>
            <th>{{ formatCurrency(sumField('february'))    }}</th>
            <th>{{ formatCurrency(sumField('march'))       }}</th>
            <th>{{ formatCurrency(sumField('april'))       }}</th>
            <th>{{ formatCurrency(sumField('may'))         }}</th>
            <th>{{ formatCurrency(sumField('june'))        }}</th>
            <th>{{ formatCurrency(sumField('july'))        }}</th>
            <th>{{ formatCurrency(sumField('august'))      }}</th>
            <th>{{ formatCurrency(sumField('september'))   }}</th>
            <th>{{ formatCurrency(sumField('october'))     }}</th>
            <th>{{ formatCurrency(sumField('november'))    }}</th>
            <th>{{ formatCurrency(sumField('december'))    }}</th>
            <th>
              {{
                formatCurrency(
                    sumField('january')
                    + sumField('february')
                    + sumField('march')
                    + sumField('april')
                    + sumField('may')
                    + sumField('june')
                    + sumField('july')
                    + sumField('august')
                    + sumField('september')
                    + sumField('october')
                    + sumField('november')
                    + sumField('december')
                )
              }}
            </th>
          </tr>
        </template>
        <template v-slot:[`item.client`]="{ item }">
          <RouterLink :to="`/client/${item.id}`">
            {{ item.client }}
          </RouterLink>
        </template>
        <template v-slot:[`item.january`]="{ item }">
          <span>{{ formatCurrency(item.january) }}</span>
        </template>
        <template v-slot:[`item.february`]="{ item }">
          <span>{{ formatCurrency(item.february) }}</span>
        </template>
        <template v-slot:[`item.march`]="{ item }">
          <span>{{ formatCurrency(item.march) }}</span>
        </template>
        <template v-slot:[`item.april`]="{ item }">
          <span>{{ formatCurrency(item.april) }}</span>
        </template>
        <template v-slot:[`item.may`]="{ item }">
          <span>{{ formatCurrency(item.may) }}</span>
        </template>
        <template v-slot:[`item.june`]="{ item }">
          <span>{{ formatCurrency(item.june) }}</span>
        </template>
        <template v-slot:[`item.july`]="{ item }">
          <span>{{ formatCurrency(item.july) }}</span>
        </template>
        <template v-slot:[`item.august`]="{ item }">
          <span>{{ formatCurrency(item.august) }}</span>
        </template>
        <template v-slot:[`item.september`]="{ item }">
          <span>{{ formatCurrency(item.september) }}</span>
        </template>
        <template v-slot:[`item.october`]="{ item }">
          <span>{{ formatCurrency(item.october) }}</span>
        </template>
        <template v-slot:[`item.november`]="{ item }">
          <span>{{ formatCurrency(item.november) }}</span>
        </template>
        <template v-slot:[`item.december`]="{ item }">
          <span>{{ formatCurrency(item.december) }}</span>
        </template>
        <template v-slot:[`item.total`]="{ item }">
          <span>{{ formatCurrency(item.total) }}</span>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
[v-cloak] {
  display: none;
}

.container {
  padding: 5px;
}

th[role="columnheader"] {
  text-transform: capitalize;
}
</style>